<template>
  <!-- The read-only details panel a hazard marker opens. The same floating
       shell as the POI sheet (app.scss .poi-sheet__*): a bottom sheet on a
       phone, a panel at the map's top-right from 992px. View-only on the web —
       confirming, clearing and reporting happen in the apps. -->
  <div v-if="open" class="hazard-sheet" :class="{ 'hazard-sheet--modal': modal }">
    <!-- On a page with no map behind it (the hike page's warnings), the panel
         is a modal: a backdrop that closes it, centred from 992px. -->
    <div v-if="modal" class="hazard-sheet__backdrop" data-test="hazard-backdrop" @click="$emit('close')"></div>
    <div
      class="poi-sheet__dialog hazard-sheet__dialog"
      :class="{ 'hazard-sheet__dialog--refreshing': refreshing }"
      :style="panelStyle"
      role="dialog"
      :aria-modal="modal ? 'true' : 'false'"
      :aria-label="details ? details.title : $t('trail_hazards.reported_hazard')"
      data-hazard-sheet
    >
      <div class="poi-sheet__content" :aria-busy="refreshing ? 'true' : 'false'">
        <div class="poi-sheet__handle" aria-hidden="true"></div>

        <!-- Opened from a card with more of its kind on the route: step
             through them (‹ 1 / 3 ›). Stays put while the next one loads. -->
        <RouteAlertPagerBar
          v-if="pager"
          :view="pager"
          @previous="$emit('pager-previous')"
          @next="$emit('pager-next')"
        />

        <!-- The hazard's icon and name share the top row with the close button,
             as in the apps (and the route-warnings sheet's title). -->
        <header class="poi-sheet__header hazard-sheet__header" :class="{ 'hazard-sheet__header--alone': !hasDetails }">
          <div v-if="loading" class="hazard-sheet__loading" role="status" data-test="hazard-loading">
            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <span>{{ $t('shared.loading') }}</span>
          </div>
          <p v-else-if="failed" class="hazard-sheet__error" role="alert" data-test="hazard-error">
            {{ $t('trail_hazards.error_generic') }}
          </p>
          <div v-else-if="details" class="hazard-sheet__identity">
            <HazardTypeIcon
              :hazard-type="details.hazardType"
              :passability="details.passability"
              :size="48"
              data-test="hazard-type-icon"
            />
            <div class="hazard-sheet__names">
              <h2 class="hazard-sheet__title" data-test="hazard-title">{{ details.title }}</h2>
              <p v-if="details.subtitle" class="hazard-sheet__subtitle" data-test="hazard-subtitle">{{ details.subtitle }}</p>
            </div>
          </div>
          <span v-else></span>
          <div class="poi-sheet__tools">
            <button
              type="button"
              class="poi-icon-btn"
              data-test="hazard-close"
              :aria-label="$t('shared.close')"
              @click="$emit('close')"
            >
              <span class="material-icons">close</span>
            </button>
          </div>
        </header>

        <div v-if="hasDetails" ref="body" class="poi-sheet__body hazard-sheet__body">
          <template v-if="details">
            <div class="hazard-tags">
              <span v-if="details.isMine" class="hazard-tag hazard-tag--mine" data-test="hazard-your-report">
                {{ $t('trail_hazards.your_report') }}
              </span>
              <span v-if="details.stateTag" class="hazard-tag" :class="`hazard-tag--${details.stateTag.kind}`" data-test="hazard-state-tag">
                <HazardIcon v-if="details.stateTag.kind === 'confirmed'" :icon="checkIcon" :size="10" color="#7DDC8F" />
                {{ details.stateTag.text }}
              </span>
              <span class="hazard-tag" :class="`hazard-tag--${details.passabilityTag.kind}`" data-test="hazard-passability-tag">
                {{ details.passabilityTag.text }}
              </span>
              <span v-if="details.reportedAgo" class="hazard-tag hazard-tag--neutral" data-test="hazard-reported-ago">
                {{ details.reportedAgo }}
              </span>
            </div>

            <div v-if="details.confirmation" class="hazard-card hazard-card--confirmed" data-test="hazard-confirmation">
              <!-- Who confirmed it, most recent first: up to three hikers, or two
                   and a +N counter (confirmerSlots). A photo, else initials, else
                   the HiiKER placeholder. Decorative: the text says the count. -->
              <span v-if="details.confirmation.avatars.length" class="hazard-confirmers" aria-hidden="true" data-test="hazard-confirmers">
                <span
                  v-for="(slot, index) in details.confirmation.avatars"
                  :key="index"
                  class="hazard-confirmer"
                  :class="[`hazard-confirmer--${slot.kind}`, { 'hazard-confirmer--long': slot.kind === 'counter' && slot.label.length > 2 }]"
                  :data-test="`hazard-confirmer-${slot.kind}`"
                >
                  <template v-if="slot.kind === 'counter'">{{ slot.label }}</template>
                  <img
                    v-else-if="slot.avatarUrl && !failedAvatars[slot.avatarUrl]"
                    :src="slot.avatarUrl"
                    alt=""
                    loading="lazy"
                    @error="failedAvatars[slot.avatarUrl] = true"
                  />
                  <template v-else-if="slot.initials">{{ slot.initials }}</template>
                  <svg v-else viewBox="0 0 89 89" class="hazard-confirmer__placeholder" data-test="hazard-confirmer-placeholder">
                    <rect width="89" height="89" fill="#FDDF00" />
                    <path :d="placeholderPath" fill="#17181D" />
                  </svg>
                </span>
              </span>
              <i18n-t
                v-if="details.confirmation.time"
                keypath="trail_hazards.confirmed_summary"
                tag="span"
                scope="global"
              >
                <template #hikers><strong>{{ details.confirmation.hikers }}</strong></template>
                <template #time>{{ details.confirmation.time }}</template>
              </i18n-t>
              <i18n-t v-else keypath="trail_hazards.confirmed_summary_no_time" tag="span" scope="global">
                <template #hikers><strong>{{ details.confirmation.hikers }}</strong></template>
              </i18n-t>
            </div>

            <div v-if="details.photos.length" class="hazard-photos" data-test="hazard-photos">
              <div class="hazard-photos__head">
                <strong>{{ $t('trail_hazards.photos_header') }}</strong>
                <span>{{ details.photoCount }}</span>
              </div>
              <div class="hazard-photos__strip">
                <button
                  v-for="(photo, index) in details.photos"
                  :key="photo.key"
                  type="button"
                  class="hazard-photos__item"
                  data-test="hazard-photo"
                  :aria-label="$t('utils.detail_sheet_photo_strip.open_photo_number_of_total', { number: index + 1, total: details.photos.length })"
                  @click="lightboxIndex = index"
                >
                  <img
                    :src="photo.thumbUrl"
                    :alt="$t('utils.detail_sheet_photo_strip.photo_number', { number: index + 1 })"
                    loading="lazy"
                    @error="$emit('photo-error')"
                  />
                </button>
              </div>
            </div>

            <!-- A div, not <footer>: app.scss paints every footer with the page
                 footer's dark background. -->
            <div class="hazard-sheet__footer" data-test="hazard-footer">
              <p class="hazard-sheet__footer-text">{{ $t('trail_hazards.app_cta') }}</p>
              <a
                class="btn btn-primary hazard-sheet__cta"
                data-test="hazard-app-cta"
                :href="appUrl"
                target="_blank"
                rel="noopener"
                @click="onAppCta"
              >{{ $t('shared.open_in_app') }}</a>
            </div>
          </template>
        </div>
      </div>
    </div>

    <DetailSheetLightbox
      :open="lightboxIndex !== null"
      :photos="details ? details.photos : []"
      :index="lightboxIndex ?? 0"
      @close="lightboxIndex = null"
      @prev="stepPhoto(-1)"
      @next="stepPhoto(1)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import HazardIcon from './HazardIcon.vue';
import HazardTypeIcon from './HazardTypeIcon.vue';
import DetailSheetLightbox from '../../../utils/shared_components/detail_sheet/DetailSheetLightbox.vue';
import { HAZARD_CHECK_ICON, HIIKER_AVATAR_PLACEHOLDER_PATH } from '../../../utils/trailHazards/hazardIcons';
import { hazardAppCtaClickedEvent, trackHazardEvent } from '../../../utils/trailHazards/hazardAnalytics';
import type { HazardDetailsView } from '../../../utils/trailHazards/hazardDetails';
import RouteAlertPagerBar from '../../route_alerts/components/RouteAlertPagerBar.vue';
import type { PagerView } from '../../../utils/routeAlerts/routeAlertGroups';

/** The page the web already sends people to for the apps ("Open in app" elsewhere). */
export const HAZARD_APP_URL = '/app';

export default defineComponent({
  name: 'HazardDetailSheet',
  components: { HazardIcon, HazardTypeIcon, DetailSheetLightbox, RouteAlertPagerBar },
  props: {
    open: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    failed: { type: Boolean, default: false },
    details: { type: Object as PropType<HazardDetailsView | null>, default: null },
    panelStyle: { type: Object as PropType<Record<string, string> | null>, default: null },
    /** A modal over the page (backdrop, centred) rather than a panel over the map. */
    modal: { type: Boolean, default: false },
    /** The pager through the card's group (‹ 1 / 3 ›); null for a hazard on its own. */
    pager: { type: Object as PropType<PagerView | null>, default: null },
    /** Stepping to the next one: these details stay, faded, until it has loaded. */
    refreshing: { type: Boolean, default: false },
  },
  emits: ['close', 'photo-error', 'pager-previous', 'pager-next'],
  computed: {
    /** The hazard itself is showing (not the loading line or the error, which sit in the header). */
    hasDetails(): boolean {
      return !!this.details && !this.loading && !this.failed;
    },
  },
  data() {
    return {
      lightboxIndex: null as number | null,
      appUrl: HAZARD_APP_URL,
      checkIcon: HAZARD_CHECK_ICON,
      placeholderPath: HIIKER_AVATAR_PLACEHOLDER_PATH,
      // A photo that fails to load falls back to the hiker's initials.
      failedAvatars: {} as Record<string, boolean>,
    };
  },
  watch: {
    // A different hazard (or none) never inherits the last one's open photo,
    // and a pager step shows the next one from its top, not where the last
    // one was scrolled to.
    'details.id'() {
      this.lightboxIndex = null;
      this.$nextTick(() => {
        const body = this.$refs.body as HTMLElement | undefined;
        if (body) body.scrollTop = 0;
      });
    },
    open(isOpen: boolean) {
      if (!isOpen) this.lightboxIndex = null;
    },
  },
  methods: {
    onAppCta() {
      if (this.details) trackHazardEvent(hazardAppCtaClickedEvent(this.details.hazardType));
    },
    stepPhoto(delta: number) {
      const count = this.details ? this.details.photos.length : 0;
      if (!count || this.lightboxIndex === null) return;
      this.lightboxIndex = (this.lightboxIndex + delta + count) % count;
    },
  },
});
</script>

<style scoped>
/* Above the POI sheet (a Bootstrap modal, 1055) so a hazard opened over a POI
   reads on top; below the lightbox (2000). */
.hazard-sheet__dialog {
  z-index: 1056;
}

/* Stepping: the last hazard's details stay, faded, until the next has loaded,
   so the panel doesn't collapse to a spinner and back on every arrow. */
.hazard-sheet__dialog--refreshing .hazard-sheet__identity,
.hazard-sheet__dialog--refreshing .hazard-sheet__body {
  opacity: 0.55;
  transition: opacity 0.15s ease;
}

/* The close button and the pager stay usable; the old hazard's photos don't. */
.hazard-sheet__dialog--refreshing .hazard-sheet__body {
  pointer-events: none;
}

.hazard-sheet__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  background: rgba(0, 0, 0, 0.5);
}

@media (min-width: 992px) {
  .hazard-sheet--modal .hazard-sheet__dialog {
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    transform: translate(-50%, -50%);
    max-height: calc(100vh - 80px);
  }
}

.hazard-sheet__body {
  padding-bottom: 1.25rem;
}

.hazard-sheet__header {
  align-items: flex-start;
}

/* Loading or an error: the header is the whole panel. */
.hazard-sheet__header--alone {
  align-items: center;
  padding-bottom: 0.85rem;
}

.hazard-tag--mine {
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
}

.hazard-sheet__loading {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--text-muted, #b2b2b2);
}

.hazard-sheet__error {
  margin: 0;
  color: #ff8a90;
}

.hazard-sheet__identity {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  padding-top: 0.15rem;
}

.hazard-sheet__names {
  min-width: 0;
}

.hazard-sheet__title {
  margin: 0;
  color: #fff;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.hazard-sheet__subtitle {
  margin: 0.2rem 0 0;
  color: #9da0ad;
  font-size: 0.85rem;
}

.hazard-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.15rem 0 0.9rem;
}

.hazard-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.hazard-tag--unconfirmed {
  color: #ffa34d;
  border: 1px dashed #f57700;
}

.hazard-tag--confirmed {
  color: #7ddc8f;
  background: rgba(46, 204, 113, 0.18);
}

.hazard-tag--passable {
  color: #ffa34d;
  background: rgba(245, 119, 0, 0.18);
}

.hazard-tag--blocked {
  color: #ff8a90;
  background: rgba(219, 43, 53, 0.18);
}

.hazard-tag--neutral {
  color: #d9d9d9;
  background: rgba(255, 255, 255, 0.08);
  font-weight: 500;
}

.hazard-card {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
  padding: 0.7rem 0.75rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #d9d9d9;
  font-size: 0.85rem;
  line-height: 1.4;
}

.hazard-card strong {
  color: #fff;
}

/* Confirmed by: the avatar stack and its text sit on one line. */
.hazard-card--confirmed {
  align-items: center;
}

/* 24px circles overlapping by 6px (enough that a middle hiker's initials stay
   readable), each ringed in the card's colour so the overlap reads as a gap.
   Later circles sit on top, so the +N counter is never covered. */
.hazard-confirmers {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
}
.hazard-confirmer {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  /* The card's own colour (the panel plus its 5% white), so the overlap reads as a gap. */
  box-shadow: 0 0 0 2px #35363b;
  background: #505259;
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
}
.hazard-confirmer + .hazard-confirmer {
  margin-left: -6px;
}
.hazard-confirmer img,
.hazard-confirmer__placeholder {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hazard-confirmer--counter {
  background: #434343;
  font-size: 9px;
}
.hazard-confirmer--long {
  font-size: 8px;
}

.hazard-card .hazard-icon {
  margin-top: 0.1rem;
}

.hazard-photos {
  margin-bottom: 0.9rem;
}

.hazard-photos__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
}

.hazard-photos__head span {
  color: #9da0ad;
  font-size: 0.75rem;
}

.hazard-photos__strip {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  scrollbar-width: none;
}

.hazard-photos__strip::-webkit-scrollbar {
  display: none;
}

.hazard-photos__item {
  flex: 0 0 auto;
  width: 96px;
  height: 72px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  cursor: zoom-in;
}

.hazard-photos__item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hazard-sheet__footer {
  margin-top: 0.4rem;
  padding-top: 0.9rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.hazard-sheet__footer-text {
  margin: 0 0 0.6rem;
  color: #d9d9d9;
  font-size: 0.85rem;
}

.hazard-sheet__cta {
  width: 100%;
}
</style>
